import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type MarkdownContent = string | string[];

interface MarkdownProps {
  children: MarkdownContent;
  className?: string;
}

/**
 * Shared markdown renderer wrapped in Tailwind `prose` styles.
 * Usage: <Markdown>{markdownText}</Markdown>
 */
export const Markdown: React.FC<MarkdownProps> = ({ children, className = '' }) => {
  return (
    <div className={`prose prose-slate lg:prose-lg prose-headings:font-heading prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:my-2 prose-p:my-3 max-w-none ${className}`.trim()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
        h1: ({ node, ...props }) => (
          <h1 className="text-4xl font-bold text-gray-900 mb-6 mt-8 first:mt-0 border-b border-gray-200 pb-3" {...props} />
        ),
        h2: ({ node, ...props }) => (
          <h2 className="text-3xl font-semibold text-gray-900 mb-4 mt-8 first:mt-0 border-b border-gray-200 pb-2" {...props} />
        ),
        h3: ({ node, ...props }) => (
          <h3 className="text-2xl font-semibold text-gray-900 mb-3 mt-6 first:mt-0" {...props} />
        ),
        h4: ({ node, ...props }) => (
          <h4 className="text-xl font-semibold text-gray-900 mb-2 mt-4 first:mt-0" {...props} />
        ),
        ul: ({ node, ...props }) => (
          <ul className="list-disc pl-6 space-y-1 my-4" {...props} />
        ),
        ol: ({ node, ...props }) => (
          <ol className="list-decimal pl-6 space-y-1 my-4" {...props} />
        ),
        code: ({ node, inline, ...props }: any) => {
          if (inline) {
            return (
              <code className="px-1.5 py-0.5 bg-gray-100 rounded text-sm font-mono text-gray-800" {...props} />
            );
          }
          return (
            <code className="block p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-sm font-mono" {...props} />
          );
        },
        a: ({ node, ...props }) => (
          <a className="text-primary-600 hover:text-primary-700 underline" {...props} />
        ),
        blockquote: ({ node, ...props }) => (
          <blockquote className="border-l-4 border-primary-500 pl-4 py-2 my-4 italic text-gray-600 bg-gray-50 rounded-r" {...props} />
        ),
        table: ({ node, ...props }) => (
          <div className="overflow-x-auto my-6">
            <table className="w-full border-collapse" {...props} />
          </div>
        ),
        thead: ({ node, ...props }) => (
          <thead {...props} />
        ),
        tbody: ({ node, ...props }) => (
          <tbody {...props} />
        ),
        tr: ({ node, ...props }) => (
          <tr className="border-b border-granite-200" {...props} />
        ),
        th: ({ node, ...props }) => (
          <th className="px-4 py-3 text-left font-heading text-sm font-semibold text-slate-700" {...props} />
        ),
        td: ({ node, ...props }) => (
          <td className="px-4 py-3 font-body text-sm text-granite-600" {...props} />
        ),
      }}
      >
        {Array.isArray(children) ? children.join('\n') : children}
      </ReactMarkdown>
    </div>
  );
};






